import { toNumber } from '../utils/numberUtils.js'
import { toYearMonth, monthKey, monthDiff, buildMonthKeysInRange } from '../utils/dateUtils.js'
import { calcularInteresRango, getSerieMetadata, getTasaMensual, proyectarConInteresCompuesto, SERIES_OFICIALES } from '../services/tasasInteresService.js'

const NORMALIZED_SERIE_CACHE = new WeakMap()

function monthsDiffBetweenKeys(a, b) {
  const ya = toYearMonth(a)
  const yb = toYearMonth(b)
  if (!ya || !yb) return Number.POSITIVE_INFINITY
  return Math.abs(monthDiff(ya, yb))
}

function getNormalizedPairs(serie) {
  if (!serie || typeof serie !== 'object') return []
  const cached = NORMALIZED_SERIE_CACHE.get(serie)
  if (cached) return cached
  const normalized = Object.entries(serie)
    .map(([k, v]) => ({ k: monthKey(k), v: Number(v) }))
    .filter((r) => r.k && Number.isFinite(r.v) && r.v > 0)
    .sort((a, b) => a.k.localeCompare(b.k))
  NORMALIZED_SERIE_CACHE.set(serie, normalized)
  return normalized
}

function findIndexValue(indiceSet, tipoIndice, key, options = {}) {
  const maxGapMonths = Number.isFinite(Number(options.maxGapMonths))
    ? Number(options.maxGapMonths)
    : 6
  const serie = indiceSet?.[tipoIndice]?.serie || {}
  if (Object.prototype.hasOwnProperty.call(serie, key)) {
    const v = Number(serie[key])
    return Number.isFinite(v) && v > 0
      ? { value: v, method: 'exact', gapMonths: 0 }
      : { value: null, method: 'missing', reason: 'exact_non_positive' }
  }

  // Soporte defensivo: algunas fuentes vienen como YYYY-MM-DD o MM/YYYY
  const target = monthKey(key)
  if (!target) return { value: null, method: 'missing', reason: 'invalid_target' }
  const normalizedPairs = getNormalizedPairs(serie)
  if (!normalizedPairs.length) {
    return { value: null, method: 'missing', reason: 'empty_series' }
  }

  const exact = normalizedPairs.find((r) => r.k === target)
  if (exact) return { value: exact.v, method: 'normalized_exact', gapMonths: 0 }

  const prev = normalizedPairs.filter((r) => r.k < target).pop()
  const next = normalizedPairs.find((r) => r.k > target)

  // Fallback preferido: interpolacion lineal entre puntos vecinos
  if (prev && next) {
    const gapPrev = monthsDiffBetweenKeys(target, prev.k)
    const gapNext = monthsDiffBetweenKeys(target, next.k)
    if (gapPrev > maxGapMonths || gapNext > maxGapMonths) {
      return {
        value: null,
        method: 'missing',
        reason: `interpolation_out_of_range_${Math.max(gapPrev, gapNext)}m`,
        gapMonths: Math.max(gapPrev, gapNext),
      }
    }
    const pYear = Number(prev.k.slice(0, 4))
    const pMonth = Number(prev.k.slice(5, 7))
    const nYear = Number(next.k.slice(0, 4))
    const nMonth = Number(next.k.slice(5, 7))
    const tYear = Number(target.slice(0, 4))
    const tMonth = Number(target.slice(5, 7))

    const prevIdx = pYear * 12 + pMonth
    const nextIdx = nYear * 12 + nMonth
    const targetIdx = tYear * 12 + tMonth
    const span = nextIdx - prevIdx
    if (span > 0) {
      const ratio = (targetIdx - prevIdx) / span
      return {
        value: Number((prev.v + (next.v - prev.v) * ratio).toFixed(8)),
        method: 'interpolated',
        gapMonths: Math.max(gapPrev, gapNext),
      }
    }
  }

  // Fallback secundario: ultimo valor conocido previo (carry-forward)
  if (prev) {
    const gapPrev = monthsDiffBetweenKeys(target, prev.k)
    if (gapPrev <= maxGapMonths) {
      return { value: prev.v, method: 'carry_forward', gapMonths: gapPrev }
    }
  }

  // Fallback final: primer valor posterior
  if (next) {
    const gapNext = monthsDiffBetweenKeys(target, next.k)
    if (gapNext <= maxGapMonths) {
      return { value: next.v, method: 'backfill', gapMonths: gapNext }
    }
    return {
      value: null,
      method: 'missing',
      reason: `backfill_out_of_range_${gapNext}m`,
      gapMonths: gapNext,
    }
  }

  // Si no hay previos, usar el primero disponible
  const first = normalizedPairs[0]
  if (first) {
    const gapFirst = monthsDiffBetweenKeys(target, first.k)
    if (gapFirst <= maxGapMonths) {
      return { value: first.v, method: 'first_available', gapMonths: gapFirst }
    }
    return {
      value: null,
      method: 'missing',
      reason: `first_available_out_of_range_${gapFirst}m`,
      gapMonths: gapFirst,
    }
  }
  return { value: null, method: 'missing', reason: 'no_match' }
}

function validateIndiceCoverage(indiceSet, indice, fromKey, toKey, options = {}) {
  const maxMissingPct = Number.isFinite(Number(options.maxMissingPct))
    ? Number(options.maxMissingPct)
    : 0.05
  const serie = indiceSet?.[indice]?.serie || {}
  const required = buildMonthKeysInRange(fromKey, toKey)
  if (!required.length) {
    return { ok: false, reason: 'invalid_range', missingPct: 100, missing: [] }
  }
  const available = new Set(
    Object.keys(serie)
      .map((k) => {
        const mm = String(k).match(/^(\d{4})-(\d{2})/)
        return mm ? `${mm[1]}-${mm[2]}` : ''
      })
      .filter(Boolean),
  )
  const missing = required.filter((k) => !available.has(k))
  const missingPct = missing.length / required.length
  return {
    ok: missingPct <= maxMissingPct,
    missingPct,
    missing,
    total: required.length,
  }
}

function yearsBetween(periodoKey, ceseKey) {
  const p = periodoKey.match(/^(\d{4})-(\d{2})$/)
  const c = ceseKey.match(/^(\d{4})-(\d{2})$/)
  if (!p || !c) return 99
  const pm = Number(p[1]) * 12 + Number(p[2])
  const cm = Number(c[1]) * 12 + Number(c[2])
  return Math.abs(cm - pm) / 12
}

function resolveIndice(tipoIndice) {
  if (tipoIndice === 'NGRI') return 'RIPTE'
  return tipoIndice
}

function resolveIndiceByRules(activeRules, fallbackIndice, fechaCese) {
  const idxCfg = activeRules?.indices?.mapeoFallos
  const cese = monthKey(fechaCese)
  const ceseYm = toYearMonth(cese)
  if (!idxCfg || !ceseYm) return resolveIndice(fallbackIndice || 'ISBIC')

  const corte = 201701
  if (ceseYm < corte) return String(idxCfg?.pre2017?.serie || 'ISBIC')
  return String(idxCfg?.post2017?.serie || 'RIPTE')
}

function minusMonthsFromDate(dateValue, months) {
  const d = new Date(dateValue)
  if (Number.isNaN(d.getTime())) return ''
  d.setMonth(d.getMonth() - Number(months || 0))
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function getThresholdConfiscatoriedad(activeRules) {
  const direct = Number(activeRules?.thresholds?.confiscatoriedad_minima)
  if (Number.isFinite(direct) && direct > 0) return direct
  const legacy = Number(activeRules?.thresholds?.confiscatoriedad)
  if (Number.isFinite(legacy) && legacy > 0) return legacy
  return 0.15
}

function median(values = []) {
  const sorted = values
    .filter((n) => Number.isFinite(n) && n > 0)
    .slice()
    .sort((a, b) => a - b)
  if (!sorted.length) return 0
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return sorted[mid]
  return (sorted[mid - 1] + sorted[mid]) / 2
}

function buildSalaryBaseline(rows = []) {
  const byCuit = new Map()
  const all = []
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    const amount = Number(row?.montoSujetoAAporte || row?.montoOriginal || row?.monto || 0)
    if (!(amount > 0)) return
    all.push(amount)
    const cuit = String(row?.cuit || '').trim()
    if (!cuit) return
    const bucket = byCuit.get(cuit) || []
    bucket.push(amount)
    byCuit.set(cuit, bucket)
  })
  const baselineByCuit = new Map()
  byCuit.forEach((vals, cuit) => {
    baselineByCuit.set(cuit, median(vals))
  })
  return {
    byCuit: baselineByCuit,
    globalMedian: median(all),
  }
}

function parseDiasRegistrados(row = {}) {
  const candidates = [row?.diasComputables, row?.diasOrigen, row?.dias]
  for (const candidate of candidates) {
    const n = Number(candidate)
    if (Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 30) {
      return n
    }
  }
  return null
}

function normalizeRemuneracionMesCompleto(row = {}, baseline = {}) {
  const diasRegistrados = parseDiasRegistrados(row)
  const base = Number(row.montoSujetoAAporte || row.montoOriginal || row.monto || 0)
  const cuit = String(row?.cuit || '').trim()
  const baselineCuit = Number(baseline?.byCuit?.get?.(cuit) || 0)
  const baselineGlobal = Number(baseline?.globalMedian || 0)
  const baselineRef = baselineCuit > 0 ? baselineCuit : baselineGlobal
  const ratioBaseVsHist = baselineRef > 0 ? (base / baselineRef) : null
  if (!(base > 0) || !diasRegistrados) {
    return {
      diasRegistrados,
      remuneracionNormalizada: null,
      remuneracionParaPromedio: base > 0 ? base : 0,
      normalizacionAplicada: false,
      escenarioNormalizacion: 'sin_datos',
      ratioBaseVsHist,
    }
  }
  if (diasRegistrados < 30) {
    const sueldoConsistente =
      ratioBaseVsHist !== null && ratioBaseVsHist >= 0.75 && ratioBaseVsHist <= 1.35
    if (sueldoConsistente) {
      return {
        diasRegistrados,
        remuneracionNormalizada: Number(base.toFixed(2)),
        remuneracionParaPromedio: Number(base.toFixed(2)),
        normalizacionAplicada: false,
        escenarioNormalizacion: 'sueldo_completo_consistente',
        ratioBaseVsHist: Number(ratioBaseVsHist.toFixed(4)),
      }
    }
    const normalizada = (base / diasRegistrados) * 30
    return {
      diasRegistrados,
      remuneracionNormalizada: Number(normalizada.toFixed(2)),
      remuneracionParaPromedio: Number(normalizada.toFixed(2)),
      normalizacionAplicada: true,
      escenarioNormalizacion: 'sueldo_parcial_normalizado',
      ratioBaseVsHist: ratioBaseVsHist === null ? null : Number(ratioBaseVsHist.toFixed(4)),
    }
  }
  return {
    diasRegistrados,
    remuneracionNormalizada: Number(base.toFixed(2)),
    remuneracionParaPromedio: Number(base.toFixed(2)),
    normalizacionAplicada: false,
    escenarioNormalizacion: 'mes_completo_30',
    ratioBaseVsHist: ratioBaseVsHist === null ? null : Number(ratioBaseVsHist.toFixed(4)),
  }
}

export default function actualizarPorIndice(rowsNormativa, tipoIndice, fechaCese, indicesMovilidad, tipoTasaInteres = 'PASIVA_14290', activeRules = null, expediente = null) {
  const data = Array.isArray(rowsNormativa) ? rowsNormativa : []
  const indice = resolveIndiceByRules(activeRules, tipoIndice || 'ISBIC', fechaCese)
  const indiceSet = indicesMovilidad && Object.keys(indicesMovilidad).length > 0 ? indicesMovilidad : {}
  const ceseKey = monthKey(fechaCese) || monthKey(new Date().toISOString())
  const ceseYm = toYearMonth(ceseKey)
  const divisorCfg = Number(activeRules?.protocol?.divisor || 120)
  const seleccionCfg = String(activeRules?.protocol?.seleccion || 'cronologico_inverso')
  const cronologicoInverso = seleccionCfg === 'cronologico_inverso'

  const fechaReclamo = expediente?.fechaReclamoAdministrativo || expediente?.fechaReclamo || ''
  const fechaOtorgamiento = expediente?.fechaOtorgamientoBeneficio || ''
  const fechaDenegatoriaODemanda = expediente?.fechaDenegatoriaODemanda || expediente?.fechaDemandaJudicial || ''
  const limiteAniosRetro = Number(activeRules?.liquidacionFinal?.retroactividad?.limiteAnios || 2)
  const requiereFechaReclamo = Boolean(activeRules?.liquidacionFinal?.retroactividad?.requiereFechaPrimerReclamoAdministrativo)
  const requiereHitosInterrupcion = Boolean(activeRules?.liquidacionFinal?.retroactividad?.requiereHitosInterrupcion)
  const hasFechaReclamo = Boolean(String(fechaReclamo || '').trim())
  const hasFechaOtorgamiento = Boolean(String(fechaOtorgamiento || '').trim())
  const hasFechaDenegatoriaODemanda = Boolean(String(fechaDenegatoriaODemanda || '').trim())
  const hitosCompletos = hasFechaReclamo && hasFechaOtorgamiento && hasFechaDenegatoriaODemanda
  const prescripcionIndeterminada = Boolean(
    (requiereFechaReclamo && !hasFechaReclamo) ||
    (requiereHitosInterrupcion && !hitosCompletos),
  )
  const limitePrescripcionKey = prescripcionIndeterminada ? '' : minusMonthsFromDate(fechaReclamo, limiteAniosRetro * 12)
  const limitePrescripcionJudicialKey = prescripcionIndeterminada ? '' : minusMonthsFromDate(fechaDenegatoriaODemanda, limiteAniosRetro * 12)
  const limitePrescripcionYm = toYearMonth(limitePrescripcionKey)
  const limitePrescripcionJudicialYm = toYearMonth(limitePrescripcionJudicialKey)
  const thresholdConfiscatoriedad = getThresholdConfiscatoriedad(activeRules)
  const detectarConfiscatoriedadTope = activeRules?.protocol?.topes?.detectarConfiscatoriedad !== false

  const serieInteres = SERIES_OFICIALES[tipoTasaInteres]?.coeficientes || SERIES_OFICIALES.PASIVA_14290.coeficientes
  const cortePreferido = activeRules?.liquidacionFinal?.fechaCorte || expediente?.fechaCorteCalculo || null
  const parsedCorte = cortePreferido ? new Date(cortePreferido) : null
  const today = parsedCorte && !Number.isNaN(parsedCorte.getTime()) ? parsedCorte : new Date()
  const metaSerie = getSerieMetadata(tipoTasaInteres, String(expediente?.jurisdiccion || 'CABA_FEDERAL'), today)
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  const indiceVersion = String(indicesMovilidad?.__canonical?.version || '')

  const filtradas = data.filter((row) => {
    const key = row.periodoKey || monthKey(row.periodo || row.periodoISO)
    const ym = toYearMonth(key)
    if (!ym) return false
    return true
  })
  const minYm = filtradas
    .map((r) => String(r.periodoKey || monthKey(r.periodo || r.periodoISO) || ''))
    .filter((k) => /^\d{4}-\d{2}$/.test(k))
    .sort()[0] || ''
  const coverageCheck = validateIndiceCoverage(
    indiceSet,
    indice,
    minYm || ceseKey,
    ceseKey,
    { maxMissingPct: 0.03 },
  )
  if (!coverageCheck.ok) {
    const preview = (coverageCheck.missing || []).slice(0, 8).join(', ')
    const msg = `Serie temporal incompleta para el período histórico solicitado (${minYm || '?'}..${ceseKey}). ` +
      `Faltan ${coverageCheck.missing?.length || 0}/${coverageCheck.total || 0} meses. Ej: ${preview}`
    return filtradas.map((row) => ({
      ...row,
      coeficiente: 0,
      montoActualizado: 0,
      montoActualizadoPromedio: 0,
      diferenciaMensual: 0,
      acumuladoConInteres: 0,
      invalidIndex: true,
      missingIndice: true,
      indiceBaseMetodo: 'missing',
      indiceCeseMetodo: 'missing',
      alertaIndice: msg,
      bloqueadoPorSerieIncompleta: true,
    }))
  }
  const salaryBaseline = buildSalaryBaseline(filtradas)
  const hasMixedContributiveCase = data.some((r) => {
    const tag = String(r?.tipoActividad || r?.tipo || r?.tipoRaw || '').toLowerCase()
    const reg = String(r?.regimenContributivo || '').toUpperCase()
    return reg === 'AUTONOMO' || tag.includes('autonom') || tag.includes('mono')
  }) && data.some((r) => {
    const tag = String(r?.tipoActividad || r?.tipo || r?.tipoRaw || '').toLowerCase()
    const reg = String(r?.regimenContributivo || '').toUpperCase()
    return reg === 'DEPENDENCIA' || tag.includes('dep')
  })

  const elegiblesPromedio = data
    .filter((r) => Number(r.monto || r.remImpSS || r.montoOriginal || 0) > 0)
    .slice()
    .sort((a, b) => {
      const ay = toYearMonth(a.periodoISO || a.periodoKey || monthKey(a.periodo || a.periodoISO)) || 0
      const by = toYearMonth(b.periodoISO || b.periodoKey || monthKey(b.periodo || b.periodoISO)) || 0
      return cronologicoInverso ? (by - ay) : (ay - by)
    })
  const selectedByPeriod = []
  const seenPeriods = new Set()
  for (const row of elegiblesPromedio) {
    const key = String(row.periodoKey || monthKey(row.periodo || row.periodoISO) || '')
    if (!key || seenPeriods.has(key)) continue
    seenPeriods.add(key)
    selectedByPeriod.push(row)
    if (selectedByPeriod.length >= Math.max(1, divisorCfg)) break
  }
  const selectedSet = new Set(selectedByPeriod.map((r) => String(r.periodoKey || monthKey(r.periodo || r.periodoISO) || '')))

  return filtradas.map((row) => {
    const periodoKey = row.periodoKey || monthKey(row.periodo || row.periodoISO)
    const periodoYm = toYearMonth(periodoKey)
    const idxPeriodoInfo = findIndexValue(indiceSet, indice, periodoKey, { maxGapMonths: 6 })
    const idxCeseInfo = findIndexValue(indiceSet, indice, ceseKey, { maxGapMonths: 6 })
    const idxPeriodo = Number(idxPeriodoInfo?.value || 0)
    const idxCese = Number(idxCeseInfo?.value || 0)
    const estimatedMap = indiceSet?.[indice]?.estimated || {}
    const isEstimatedIndex = Boolean(estimatedMap[periodoKey] || estimatedMap[ceseKey])
    const tipoActividadTag = String(row?.tipoActividad || row?.tipo || row?.tipoRaw || '').toLowerCase()
    const esAutonomo = tipoActividadTag.includes('autonom') || tipoActividadTag.includes('mono')
    const doctrinaAutonomos = esAutonomo ? 'volonte_makler' : ''

    const weakMethods = new Set(['first_available', 'backfill', 'missing'])
    const weakBase = weakMethods.has(String(idxPeriodoInfo?.method || 'missing'))
    const weakCese = weakMethods.has(String(idxCeseInfo?.method || 'missing'))
    const weakAny = weakBase || weakCese

    let coeficiente = null
    if (!weakAny && idxPeriodo && idxCese && idxPeriodo > 0 && idxCese > 0) {
      coeficiente = idxCese / idxPeriodo
    }

    if (indice === 'ISBIC' && esAutonomo && hasMixedContributiveCase) {
      coeficiente = 1
    }

    // Empalme defensivo para ultimos 10 anios
    if (yearsBetween(periodoKey, ceseKey) <= 10 && indice === 'ISBIC') {
      const rpInfo = findIndexValue(indiceSet, 'RIPTE', periodoKey, { maxGapMonths: 6 })
      const rcInfo = findIndexValue(indiceSet, 'RIPTE', ceseKey, { maxGapMonths: 6 })
      const rp = Number(rpInfo?.value || 0)
      const rc = Number(rcInfo?.value || 0)
      const weakRipte = weakMethods.has(String(rpInfo?.method || 'missing')) ||
        weakMethods.has(String(rcInfo?.method || 'missing'))
      if (!weakRipte && rp && rc && rp > 0 && rc > 0) {
        coeficiente = rc / rp
      }
    }

    const missingIndice = !(idxPeriodo && idxCese && idxPeriodo > 0 && idxCese > 0)
    const simulationRow = Boolean(row?.__simulation)
    const ym = toYearMonth(periodoKey)
    const coefFueraDeRango = Number.isFinite(coeficiente) && coeficiente > 8
    const invalidIndex = simulationRow ? false : (missingIndice || coefFueraDeRango || weakAny)
    if (!Number.isFinite(coeficiente) || coeficiente <= 0 || weakAny) coeficiente = null
    if (coefFueraDeRango) coeficiente = null

    const baseTopada = Number(row.montoSujetoAAporte || 0)
    const baseSinTope = Number(row.montoRealSinTope || row.montoOriginal || baseTopada || 0)
    const montoActualizadoTopado = coeficiente ? baseTopada * coeficiente : baseTopada
    const montoActualizadoSinTope = coeficiente ? baseSinTope * coeficiente : baseSinTope
    const quitaPorTopePeriodoPct = montoActualizadoSinTope > 0
      ? (Math.max(0, montoActualizadoSinTope - montoActualizadoTopado) / montoActualizadoSinTope)
      : 0
    const topeConfiscatorio = Boolean(
      detectarConfiscatoriedadTope &&
      Boolean(row.isTopado) &&
      quitaPorTopePeriodoPct > thresholdConfiscatoriedad,
    )
    const baseAplicada = topeConfiscatorio ? baseSinTope : baseTopada
    const promedioAdapter = normalizeRemuneracionMesCompleto({
      ...row,
      montoSujetoAAporte: baseAplicada,
    }, salaryBaseline)
    const montoActualizado = coeficiente ? baseAplicada * coeficiente : null
    const montoActualizadoPromedio = coeficiente ?
      (promedioAdapter.remuneracionParaPromedio * coeficiente) :
      null
    const diferenciaMensual = invalidIndex ? 0 : Math.max(
      0,
      (Number.isFinite(montoActualizado) ? montoActualizado : 0) -
        Number(row.montoOriginal || 0),
    )
    const periodoRef = row.periodoKey || row.periodo || row.periodoISO || ''
    const tasaMensual = getTasaMensual(periodoRef, tipoTasaInteres)
    const fueraPrescripcionAdmin = Boolean(limitePrescripcionYm && periodoYm && periodoYm < limitePrescripcionYm)
    const fueraPrescripcionJudicial = Boolean(limitePrescripcionJudicialYm && periodoYm && periodoYm < limitePrescripcionJudicialYm)
    const fueraPrescripcion = Boolean(fueraPrescripcionAdmin && fueraPrescripcionJudicial)
    const diferenciaAplicable = fueraPrescripcion ? 0 : diferenciaMensual
    const acumuladoConInteres = invalidIndex ? 0 :
      (fueraPrescripcion ? 0 : proyectarConInteresCompuesto(
        diferenciaMensual,
        periodoRef,
        tipoTasaInteres,
        today,
      ))
    const factorInteres = calcularInteresRango(periodoRef, todayKey, serieInteres)
    const baseOriginal = Number(row.montoOriginal || 0)
    const deltaPct = baseOriginal > 0 ? (diferenciaAplicable / baseOriginal) : 0
    const isConfiscatory = deltaPct > thresholdConfiscatoriedad
    const inSelected120 = selectedSet.has(String(periodoKey || ''))

    return {
      ...row,
      incluirEnPromedio: inSelected120,
      diasRegistrados: promedioAdapter.diasRegistrados,
      normalizacionMesParcialAplicada: promedioAdapter.normalizacionAplicada,
      escenarioNormalizacionMesParcial: promedioAdapter.escenarioNormalizacion,
      ratioSalarialVsHistorico: promedioAdapter.ratioBaseVsHist,
      montoBasePromedio: Number(promedioAdapter.remuneracionParaPromedio.toFixed(2)),
      montoNormalizadoMesCompleto: promedioAdapter.remuneracionNormalizada,
      montoActualizadoPromedio: Number.isFinite(montoActualizadoPromedio) ?
        Number(montoActualizadoPromedio.toFixed(2)) : 0,
      coeficiente: coeficiente ? Number(coeficiente.toFixed(6)) : 0,
      montoActualizado: Number.isFinite(montoActualizado) ?
        Number(montoActualizado.toFixed(2)) : 0,
      indiceAplicado: indice,
      diferenciaMensual: Number(diferenciaAplicable.toFixed(2)),
      tasaInteresMensual: Number(tasaMensual.toFixed(6)),
      tasaInteresTipo: tipoTasaInteres,
      tasaInteresFuente: metaSerie.fuente,
      tasaInteresFechaCorte: metaSerie.fechaCorte,
      tasaInteresJurisdiccion: metaSerie.jurisdiccion,
      tasaInteresCriterio: metaSerie.criterio,
      tasaInteresSala: metaSerie.sala,
      tasaInteresPrecedente: metaSerie.precedente,
      tasaInteresProfileKey: metaSerie.profileKey,
      tasaInteresVigente: metaSerie.vigente,
      fechaCorteCalculo: todayKey,
      indiceVersion,
      factorInteres: Number(factorInteres.toFixed(8)),
      acumuladoConInteres: Number(acumuladoConInteres.toFixed(2)),
      indiceBaseValor: Number(idxPeriodo || 0),
      indiceCeseValor: Number(idxCese || 0),
      indiceBaseMetodo: indice === 'ISBIC' && esAutonomo && hasMixedContributiveCase ? 'autonomo_categoria_vigente' : String(idxPeriodoInfo?.method || 'missing'),
      indiceCeseMetodo: indice === 'ISBIC' && esAutonomo && hasMixedContributiveCase ? 'autonomo_categoria_vigente' : String(idxCeseInfo?.method || 'missing'),
      missingIndice: simulationRow ? false : missingIndice,
      invalidIndex,
      alertaIndice: invalidIndex ?
        (coefFueraDeRango ?
          `Coeficiente fuera de rango para ${periodoKey}` :
          `Serie temporal incompleta para el período histórico solicitado (${periodoKey}). ` +
          `Base=${String(idxPeriodoInfo?.method || 'missing')} Cese=${String(idxCeseInfo?.method || 'missing')}.`) :
        '',
      isEstimatedIndex,
      isEstimated: isEstimatedIndex,
      bloqueadoPorSerieIncompleta: invalidIndex,
      doctrinaAutonomos,
      montoActualizadoTopado: Number.isFinite(montoActualizadoTopado) ? Number(montoActualizadoTopado.toFixed(2)) : 0,
      montoActualizadoSinTope: Number.isFinite(montoActualizadoSinTope) ? Number(montoActualizadoSinTope.toFixed(2)) : 0,
      quitaPorTopePeriodoPct: Number((quitaPorTopePeriodoPct * 100).toFixed(2)),
      topeConfiscatorio,
      topeInaplicablePorConfiscatoriedad: topeConfiscatorio,
      criterioTopeAplicado: topeConfiscatorio ? 'inaplicado_por_confiscatoriedad_periodo' : 'tope_legal_art9',
      fueraPrescripcion,
      fueraPrescripcionAdmin,
      fueraPrescripcionJudicial,
      prescripcionIndeterminada,
      prescripcionHitosCompletos: hitosCompletos,
      alertaPrescripcion: prescripcionIndeterminada ? 'Faltan hitos de interrupcion de prescripcion (alta, reclamo administrativo, denegatoria/demanda).' : '',
      isConfiscatory,
      isConfiscatoryByTope: topeConfiscatorio,
    }
  }).sort((a, b) => {
    const ay = toYearMonth(a.periodoKey || monthKey(a.periodo || a.periodoISO)) || 0
    const by = toYearMonth(b.periodoKey || monthKey(b.periodo || b.periodoISO)) || 0
    return by - ay
  })
}
